import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, ProtectedRoute } from "./contexts/AuthContext";
import { ThemeProvider } from "./components/theme-provider";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Layout from "./components/layout/Layout";

// Lazy load all page components for code splitting
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AssistentesIA = lazy(() => import("./pages/AssistentesIA"));
const ChatAssistente = lazy(() => import("./pages/ChatAssistente"));
const LivesGravadas = lazy(() => import("./pages/LivesGravadas"));
const Cases = lazy(() => import("./pages/Cases"));
const MemberProfile = lazy(() => import("./pages/MemberProfile"));
const Duvidas = lazy(() => import("./pages/Duvidas"));
const Regras = lazy(() => import("./pages/Regras"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const Moderation = lazy(() => import("./pages/Moderation"));
const GamificationAdmin = lazy(() => import("./pages/GamificationAdmin"));
const Ranking = lazy(() => import("./pages/Ranking"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Courses = lazy(() => import("./pages/Courses"));
const CoursePlayer = lazy(() => import("./pages/CoursePlayer"));
const AdminCourses = lazy(() => import("./pages/admin/AdminCourses"));
const AdminCourseEdit = lazy(() => import("./pages/admin/AdminCourseEdit"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminLives = lazy(() => import("./pages/admin/AdminLives"));
const AdminLiveEdit = lazy(() => import("./pages/admin/AdminLiveEdit"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AIReports = lazy(() => import("./pages/AIReports"));
const Challenges = lazy(() => import("./pages/Challenges"));
const Messages = lazy(() => import("./pages/Messages"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Login />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />

                  {/* Pricing (accessible to all authenticated users) */}
                  <Route path="/pricing" element={
                    <ProtectedRoute>
                      <Layout><Pricing /></Layout>
                    </ProtectedRoute>
                  } />

                  {/* Protected Routes */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <Layout><Dashboard /></Layout>
                    </ProtectedRoute>
                  } />


                  <Route path="/assistentes" element={
                    <ProtectedRoute>
                      <Layout><AssistentesIA /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/membro/:userId" element={
                    <ProtectedRoute>
                      <Layout><MemberProfile /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/chat-assistente" element={
                    <ProtectedRoute>
                      <Layout><ChatAssistente /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/assistentes-ia/chat/:assistantId" element={
                    <ProtectedRoute>
                      <Layout><ChatAssistente /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/lives" element={
                    <ProtectedRoute>
                      <Layout><LivesGravadas /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/cases" element={
                    <ProtectedRoute>
                      <Layout><Cases /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/duvidas" element={
                    <ProtectedRoute>
                      <Layout><Duvidas /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/regras" element={
                    <ProtectedRoute>
                      <Layout><Regras /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/perfil" element={
                    <ProtectedRoute>
                      <Layout><Profile /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/profile/:id" element={
                    <ProtectedRoute>
                      <Layout><Profile /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <Layout><Settings /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/cursos" element={
                    <ProtectedRoute>
                      <Layout><Courses /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/cursos/:courseSlug" element={
                    <ProtectedRoute>
                      <CoursePlayer />
                    </ProtectedRoute>
                  } />
                  <Route path="/cursos/:courseSlug/aula/:lessonId" element={
                    <ProtectedRoute>
                      <Layout><CoursePlayer /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/moderation" element={
                    <ProtectedRoute>
                      <Layout><Moderation /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/gamification-admin" element={
                    <ProtectedRoute>
                      <Layout><GamificationAdmin /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/ranking" element={
                    <ProtectedRoute>
                      <Layout><Ranking /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/courses" element={
                    <ProtectedRoute>
                      <Layout><AdminCourses /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/courses/new" element={
                    <ProtectedRoute>
                      <Layout><AdminCourseEdit /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/courses/:id" element={
                    <ProtectedRoute>
                      <Layout><AdminCourseEdit /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/lives" element={
                    <ProtectedRoute>
                      <Layout><AdminLives /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/lives/new" element={
                    <ProtectedRoute>
                      <Layout><AdminLiveEdit /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/lives/:id" element={
                    <ProtectedRoute>
                      <Layout><AdminLiveEdit /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/users" element={
                    <ProtectedRoute>
                      <Layout><AdminUsers /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/dashboard" element={
                    <ProtectedRoute>
                      <Layout><AdminDashboard /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/relatorios" element={
                    <ProtectedRoute>
                      <Layout><AdminReports /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/meus-relatorios" element={
                    <ProtectedRoute>
                      <Layout><AIReports /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/desafios" element={
                    <ProtectedRoute>
                      <Layout><Challenges /></Layout>
                    </ProtectedRoute>
                  } />
                  <Route path="/mensagens" element={
                    <ProtectedRoute>
                      <Layout><Messages /></Layout>
                    </ProtectedRoute>
                  } />

                  {/* Catch-all */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
